import { useContext, useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { List } from 'immutable';
import { getTemplateId } from 'conversations-message-history/common-message-format/operators/emailMetadataGetters';
import { useCommonReplyForEditor } from '../../../common-reply/protected/hooks';
import { mergeInsertMetadata } from '../operators/mergeInsertMetadata';
import { isTestThread as isTestThreadOperator, getIsAssignedToBot, getAssignee, getThreadId, getInboxId, getOriginalChannelInstanceId } from 'conversations-thread-data/thread-details/public/operators';
import { ReplySubmitButton } from '../../../send-button/public/components';
import { buildPublishableCommonMessage } from 'conversations-thread-data/common-reply/public/operators/buildPublishableCommonMessage';
import { getCommonMessage, getReplyId } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { MetadataBar } from '../../../metadata-editor/protected/components';
import { useEditorCapabilitiesConfig } from '../hooks/useEditorCapabilitiesConfig';
import ErrorBoundary from 'conversations-error-reporting/error-boundaries/components/ErrorBoundary';
import { isPotentiallyRecoverableEditorCrash } from '../../../prosemirror/protected/operators';
import { LogLevel } from 'conversations-error-reporting/error-reporting/constants/LogLevel';
import { BaseRichTextEditorError, CapabilitiesEditorPlaceholder } from '../../../editor-utilities/protected/components';
import RichTextEditorProvider from './RichTextEditorProvider';
import { getId as getCommonMessageId, getAllRecipientsDeliveryIdentifiers, getRecipients, getGenericChannelId } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
// @ts-expect-error module not typed
import { getTimestamp } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
// TODO: move to composer-ui
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { getRequiredMessagePositionDescriptor } from 'conversations-thread-data/channel-configuration/public/operators';
import { useContactPermissions } from 'conversations-thread-data/contact-permissions/public/hooks';
// TODO: move to composer-ui
import { getLatestAgentCommonMessageByGenericChannelId } from 'conversations-thread-view/thread-histories/selectors/getLatestAgentCommonMessageByGenericChannelId';
import { BASE_EDITOR_HEIGHT } from '../constants/replyEditorHeights';
import { useEmailDeliverabilityStatuses } from 'composer-data/email/public/hooks';
import { EMAIL_GENERIC_CHANNEL_ID } from 'conversations-thread-data/generic-channels/public/constants';
// @ts-expect-error module not typed
import { useContactInfoForEditor } from '../hooks/useContactInfoForEditor';
import { CompositionMethod } from './CompositionMethod';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { editorDraftHasAnyContent } from '../../../editor-state/protected/operators';
import { getTicketIdFromPathname } from '../operators/getTicketIdFromPathname';
import { trackStandaloneEvent } from '../../../usage-tracking/clients/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
const MAX_CRASH_RETRY_COUNT = 2;
function renderError(props) {
  return /*#__PURE__*/_jsx(BaseRichTextEditorError, Object.assign({}, props));
}
const noop = () => {};
const BaseCapabilitiesEditor = props => {
  const {
    canSubmit,
    className,
    emptyReply,
    getDisabledProps,
    onChangeReplyChannel,
    onCloseShepherd,
    onDraftChange = noop,
    onSubmit = noop,
    onTyping = noop,
    reconcileReplies,
    renderSubmitButton = ReplySubmitButton,
    threadDetails,
    isMetadataEditorAbsolute,
    isComposer,
    renderExistingFileSelect,
    replyRecommendationsEnabled,
    useWhatsAppCompose,
    defaultSendFromEnabled,
    macrosEnabled
  } = props;
  const threadId = getThreadId(threadDetails);
  const inboxId = getInboxId(threadDetails);
  const ticketId = getTicketIdFromPathname();
  const {
    idType,
    id: idNumber
  } = useMemo(() => {
    if (Number(threadId)) {
      return {
        idType: 'THREAD_ID',
        id: Number(threadId)
      };
    }
    if (Number(ticketId)) {
      return {
        idType: 'TICKET_ID',
        id: Number(ticketId)
      };
    }
    if (Number(inboxId)) {
      return {
        idType: 'WORKSPACE_ID',
        id: Number(inboxId)
      };
    }
    return {
      idType: undefined,
      id: undefined
    };
  }, [threadId, inboxId, ticketId]);
  const originalChannelInstanceId = getOriginalChannelInstanceId(threadDetails);
  const [crashRetryCount, setCrashRetryCount] = useState(0);
  const {
    genericChannelId,
    capabilitiesBlob
  } = useContext(ChannelCapabilitiesConfigContext);
  const requireMessagePositionDescriptor = getRequiredMessagePositionDescriptor(capabilitiesBlob);
  const latestAgentCommonMessageByGenericChannelId = useSelector(state => {
    if (requireMessagePositionDescriptor) {
      /*  @ts-expect-error Jun-10-2024, 18:11UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */
      return getLatestAgentCommonMessageByGenericChannelId(state, {
        threadId
      });
    }
    return null;
  });
  const {
    channelType,
    placeholder,
    editorMaxContentSizeLimit,
    isEditorTypingIndicatorEnabled
  } = useEditorCapabilitiesConfig();
  const handleSubmitWithCMF = useCallback((reply, {
    communicatorType,
    html = '',
    plainText = '',
    options
  }) => {
    const messageFromReply = getCommonMessage(reply); // TSFixMe when `getCommonMessage` is correctly typed;
    const templateId = messageFromReply && getTemplateId(messageFromReply) || null;
    const latestAgentCommonMessageSentAt = latestAgentCommonMessageByGenericChannelId && getTimestamp(latestAgentCommonMessageByGenericChannelId);
    const latestAgentCommonMessageIdWithin7Days = latestAgentCommonMessageSentAt && latestAgentCommonMessageSentAt - Date.now() < SEVEN_DAYS ? getCommonMessageId(latestAgentCommonMessageByGenericChannelId) : null;
    const message = buildPublishableCommonMessage(reply, {
      html,
      plainText
    }, {
      latestAgentCommonMessageId: latestAgentCommonMessageIdWithin7Days,
      requireMessagePositionDescriptor
    });
    onSubmit(message, {
      communicatorType,
      options: Object.assign({}, options, {
        templateId
      })
    });
  }, [latestAgentCommonMessageByGenericChannelId, onSubmit, requireMessagePositionDescriptor]);

  // Get the CommonReply state and handlers to update it.
  const {
    onChange: handleChange,
    onSubmit: handleSubmit,
    reply,
    hasDraftContent,
    loadedPersistentDrafts,
    savePersistentDraft,
    clearPersistentDraft,
    draftSaveFailed
  } = useCommonReplyForEditor({
    emptyReply,
    onDraftChange,
    onSubmit: handleSubmitWithCMF,
    reconcileReplies,
    onChangeReplyChannel,
    idType,
    idNumber
  });
  const handleInsert = useCallback((insertMetadata = {}) => {
    handleChange(currentReply => {
      return mergeInsertMetadata(insertMetadata, currentReply);
    });
  }, [handleChange]);
  const handleTyping = useCallback(() => {
    if (Boolean(getAssignee(threadDetails)) && !getIsAssignedToBot(threadDetails) && isEditorTypingIndicatorEnabled) {
      onTyping();
    }
  }, [onTyping, threadDetails, isEditorTypingIndicatorEnabled]);
  const commonMessage = getCommonMessage(reply);
  const emailAddresses = useMemo(() => {
    // ensure draft message is handled by the current editor configuration
    return getGenericChannelId(commonMessage) === EMAIL_GENERIC_CHANNEL_ID ? getAllRecipientsDeliveryIdentifiers(commonMessage) : List();
  }, [commonMessage]);
  const {
    deliverabilityStatuses
  } = useEmailDeliverabilityStatuses({
    emailAddresses: emailAddresses.toArray(),
    deferred: genericChannelId !== EMAIL_GENERIC_CHANNEL_ID
  });
  const {
    contactVid,
    contactEmail
  } = useContactInfoForEditor({
    emailAddresses,
    threadDetails
  });
  const uniqueEmailAddresses = emailAddresses.toSet();
  const {
    contactPermissions
  } = useContactPermissions({
    emailAddresses: uniqueEmailAddresses
  });

  // manage submission disabling
  const isTestThread = isTestThreadOperator(threadDetails);

  // Build UI hooks for underlying plugins to interact with/control the editor.
  // This is principally intended for the popup email communicator.
  const replyRef = useRef(reply);
  useEffect(() => {
    replyRef.current = reply;
  }, [reply]);
  const metadataBarRef = useRef(null);
  const renderSubmitButtonWithProps = useCallback(buttonProps => {
    const recipients = getRecipients(commonMessage) || List();
    const recipientCount = Math.max(recipients.size, 1); // <= 1 = Send, 1+ = Send to n

    return renderSubmitButton(Object.assign({}, buttonProps, {
      channelType,
      genericChannelId,
      recipientCount,
      threadId
    }));
  }, [channelType, genericChannelId, renderSubmitButton, threadId, commonMessage]);
  const shouldResetCrash = error => {
    if (crashRetryCount < MAX_CRASH_RETRY_COUNT) {
      setCrashRetryCount(crashRetryCount + 1);
      return isPotentiallyRecoverableEditorCrash(error);
    }
    return false;
  };
  const getLogLevel = error => {
    const {
      message
    } = error;
    if (message.includes('removeChild')) {
      return LogLevel.info;
    }
    return LogLevel.error;
  };
  const onGetComponentDataForLogging = () => {
    const {
      expanded
    } = metadataBarRef.current || {
      expanded: false
    };
    return {
      inboxId,
      threadId,
      isComposer,
      expanded,
      genericChannelId
    };
  };
  const getDisabledPropsCallback = useCallback(args => {
    return getDisabledProps(Object.assign({
      canSubmit,
      contactPermissions,
      deliverabilityStatuses,
      isTestThread,
      reply
    }, args || {}));
  }, [canSubmit, contactPermissions, deliverabilityStatuses, isTestThread, reply, getDisabledProps]);
  const replyId = getReplyId(reply);
  const {
    onShrink: handleShrink
  } = metadataBarRef.current || {
    onShrink: () => {}
  };
  const channelAllowsMetadataEdit = genericChannelId === EMAIL_GENERIC_CHANNEL_ID;
  const displayMetadataBar = channelAllowsMetadataEdit && !isTestThread || isComposer;
  const hasDraftEditorContent = loadedPersistentDrafts && hasDraftContent && !editorDraftHasAnyContent(reply.editorState);

  // Be careful changing this logic.
  // It's important that we don't block the UI if the draft ever fails to load in a reasonable amount of time.
  // The loading state timeout is 4s
  const persistentDraftStillLoading = loadedPersistentDrafts === false || hasDraftEditorContent;
  useEffect(() => {
    if (hasDraftEditorContent) {
      var _genericChannelId$toS;
      trackStandaloneEvent('Conversations', 'Communicator Interaction', {
        action: 'draft revisited',
        channel: (_genericChannelId$toS = genericChannelId === null || genericChannelId === void 0 ? void 0 : genericChannelId.toString()) !== null && _genericChannelId$toS !== void 0 ? _genericChannelId$toS : ''
      });
    }
  }, [hasDraftEditorContent, genericChannelId]);
  const richTextEditor = persistentDraftStillLoading ? /*#__PURE__*/_jsx(LoadingOverlay, {
    use: "dots",
    contextual: true
  }) : /*#__PURE__*/_jsx(RichTextEditorProvider, {
    className: className,
    contactEmail: contactEmail,
    contactVid: contactVid,
    getDisabledProps: getDisabledPropsCallback
    /*  @ts-expect-error Jun-10-2024, 18:11UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */,
    inboxId: inboxId,
    maxSize: editorMaxContentSizeLimit,
    minHeight: BASE_EDITOR_HEIGHT,
    onChange: handleChange,
    onCloseShepherd: onCloseShepherd,
    onFocus: handleShrink,
    onInsert: handleInsert,
    onSubmit: handleSubmit,
    onTyping: handleTyping,
    originalChannelInstanceId: originalChannelInstanceId,
    placeholder: /*#__PURE__*/_jsx(CapabilitiesEditorPlaceholder, {
      placeholder: placeholder
    }),
    renderExistingFileSelect: renderExistingFileSelect,
    renderSubmitButton: renderSubmitButtonWithProps,
    reply: reply,
    replyRecommendationsEnabled: replyRecommendationsEnabled,
    macrosEnabled: macrosEnabled
    /*  @ts-expect-error Jun-10-2024, 18:11UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */,
    threadId: threadId,
    savePersistentDraft: savePersistentDraft,
    clearPersistentDraft: clearPersistentDraft,
    draftSaveFailed: draftSaveFailed
  }, replyId);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [displayMetadataBar && /*#__PURE__*/_jsx(MetadataBar, {
      ref: metadataBarRef
      /*  @ts-expect-error Jun-10-2024, 18:11UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */,
      inboxId: inboxId,
      reply: reply,
      handleChange: handleChange,
      isComposer: isComposer,
      isMetadataEditorAbsolute: isMetadataEditorAbsolute,
      useWhatsAppCompose: useWhatsAppCompose,
      defaultSendFromEnabled: defaultSendFromEnabled
    }), /*#__PURE__*/_jsx(ErrorBoundary, {
      renderError: renderError,
      errorLoggingTitle: "An uncaught Conversations Editor error occurred",
      errorBoundaryName: 'EditorErrorBoundary',
      shouldAutoReset: shouldResetCrash,
      getLogLevel: getLogLevel,
      onGetComponentDataForLogging: onGetComponentDataForLogging,
      children: useWhatsAppCompose ? /*#__PURE__*/_jsx(CompositionMethod, {
        reply: reply
        /*  @ts-expect-error Jun-10-2024, 18:11UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */,
        inboxId: inboxId,
        richTextEditor: richTextEditor,
        onSend: handleSubmit
      }) : richTextEditor
    })]
  });
};
BaseCapabilitiesEditor.displayName = 'BaseCapabilitiesEditor';
export default BaseCapabilitiesEditor;